import { useState } from "react";

import { Button, Grid, Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  getAdminUsers,
  resendResetPasswordLink,
  resetUserRetryCount,
  updateUserStatus,
} from "../../Api/User/User.Api";
import { ReactComponent as Person } from "../../Assets/Images/Icon/PersonICON.svg";
import SearchInput from "../../Components/atoms/Input/SearchInput";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import { NewUser } from "../../Components/organisms/User/NewUser";
import useApiData from "../../Utils/Hooks/useApiData";
import useLoadingState from "../../Utils/Hooks/useLodingState";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getColumns } from "./Columns";

export const Users = () => {
  const { t } = useTranslation();

  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();

  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: 0,
    totalRecordsCount: 0,
  });
  const [searchText, setSearchText] = useState("");

  const apiCall = async () => {
    const response = await getAdminUsers({
      page: pageData.currentPage,
      size: pageData.pageSize,
      searchText: searchText,
    });
    if (response.type == 0) {
      return response;
    } else {
      throw new Error(response.message || "Failed to fetch user data");
    }
  };

  const { isLoading, data, error, refetch } = useApiData({
    apiCall: apiCall,
    dependency: [pageData.currentPage, pageData.pageSize, searchText],
    pagination: {
      isServerSidePagination: true,
      setApiData: (totalRecordsCount, pageSize, pageNumber, pagesCount) => {
        if (
          pageData.currentPage !== pageNumber ||
          pageSize !== pageData.pageSize
        ) {
          setPageData({
            ...pageData,
            currentPage:
              pageData.currentPage !== pageNumber
                ? pageNumber
                : pageData.currentPage,
            pageSize:
              pageSize !== pageData.pageSize ? pageSize : pageData.pageSize,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        } else {
          setPageData({
            ...pageData,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        }
      },
    },
  });

  const clickHandler = async (e, data) => {
    // e represent index of action menu options
    if (e != 1) startLoading({ btnName: "load" });
    if (e === 1) {
      openOverlay({
        mode: "view-edit",
        OverlayName: "new-user",
        record: data,
      });
    } else if (e === 2) {
      await resendResetPasswordLink({
        user: {
          email: data?.email,
          userCode: data?.userIdentifier,
          roleName: data?.roleName,
        },
      }).then((response) => {
        openOverlay({
          OverlayName: "reset-password",
          mode: response.type != 0 && "fail",
        });
      });
    } else if (e === 3) {
      await updateUserStatus({
        email: data?.email,
        userCode: data?.userIdentifier,
        status: false,
      }).then((response) => {
        openOverlay({
          OverlayName: "inactivate-account",
          mode: response.type !== 0 && "fail",
        });
        response.type == 0 && refetch();
      });
    } else if (e === 4) {
      await updateUserStatus({
        email: data?.email,
        userCode: data?.userIdentifier,
        status: true,
      }).then((response) => {
        openOverlay({
          OverlayName: "activate-account",
          mode: response.type !== 0 && "fail",
        });
        response.type == 0 && refetch();
      });
    } else if (e === 5) {
      await resetUserRetryCount(data?.email).then((response) => {
        openOverlay({
          OverlayName: "reset-otp-count",
          mode: response.type !== 0 && "fail",
        });

        response.type == 0 && refetch();
      });
    }
    stopLoading();
  };

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"row"} justifyContent={"space-between"} mb={2.8}>
            <Heading1 text={t("glossary:user.title")} />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <SearchInput
                width={"319.42px"}
                searchValue={searchText}
                placeholder={t("common:placeholder.userid-name-email")}
                getSearchText={(val) => {
                  setSearchText(val);
                  setPageData({ ...pageData, currentPage: 1 });
                }}
              />

              <Button
                disableTouchRipple
                variant="lightBlue"
                sx={{ borderRadius: "14px" }}
                onClick={() => {
                  openOverlay({ OverlayName: "new-user" });
                }}
              >
                <Person style={{ marginRight: "10px" }} fill="#ffffff" />
                <Heading3 text={t("common:button.new-user")} color="#FFFFFF" />
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item width="100%">
          <Paper
            sx={{
              padding: "20px 24px",
              boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
              position: "relative",
              height: "100%",
            }}
          >
            <BasicGrid
              // isError={error}
              isLoading={isLoading || LoadingState.visible}
              rowSelect={false}
              data={data}
              pageData={pageData}
              columnArray={getColumns(t, clickHandler)}
              allowSorting
              allowPaging
              customSearchText={""}
              field={["userIdentifier", "fullName"]}
              paginationClick={(pageNumber) => {
                setPageData({ ...pageData, currentPage: pageNumber });
              }}
              pageSizeChange={(selectedPageSize) => {
                setPageData({ ...pageData, pageSize: selectedPageSize });
              }}
            />
          </Paper>
        </Grid>
      </Grid>
      {OverlayState.visible && OverlayState.OverlayName === "new-user" && (
        <NewUser
          t={t}
          close={closeOverlay}
          OpenDrawer={OverlayState.visible}
          refetch={refetch}
          record={OverlayState.mode === "view-edit" && OverlayState.record}
          mode={OverlayState.mode}
        />
      )}
      {OverlayState.visible &&
        OverlayState.OverlayName === "reset-password" && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail"}
            title={t("glossary:user.action.reset-password")}
            description={
              OverlayState.mode !== "fail"
                ? t("glossary:user.action.reset-password-desc")
                : t("validation:password-link-send-fail")
            }
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}
      {OverlayState.visible &&
        OverlayState.OverlayName === "inactivate-account" && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail"}
            title={t("glossary:user.action.inactivate-account")}
            description={
              OverlayState.mode !== "fail"
                ? t("glossary:user.action.inactivate-account-desc")
                : t("validation:inactive-fail")
            }
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}
      {OverlayState.visible &&
        OverlayState.OverlayName === "activate-account" && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail"}
            title={t("glossary:user.action.activate-account")}
            description={
              OverlayState.mode !== "fail"
                ? t("glossary:user.action.activate-account-desc")
                : t("validation:active-fail")
            }
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}{" "}
      {OverlayState.visible &&
        OverlayState.OverlayName === "reset-otp-count" && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail"}
            title={t("glossary:auth.reset-otp-count.reset-otp-count")}
            description={
              OverlayState.mode !== "fail"
                ? t("glossary:auth.reset-otp-count.succeed-message")
                : t("glossary:auth.reset-otp-count.unsucceed-message")
            }
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}
    </>
  );
};
